import type { AgentImport } from "@/schemas/agent.schema";
import { useFileDialog } from "@vueuse/core";
import omit from "lodash/omit";
import { watchEffect } from "vue";
import { z } from "zod";
import { toaster } from "@/components/providers/ToasterProvider";
import { resolveImportedMcpServersFromApi } from "@/pages/agents/form/mcp-servers/mcp-server-transfer";
import { $t } from "@/services/i18n";
import { useProjectStore } from "@/stores/project.store";
import { createJsonParser } from "@/utils/json";

export const useImportAgent = (options: { setAgent: (agent: AgentImport) => void }) => {
  const projectStore = useProjectStore();
  const { files, open, reset } = useFileDialog({
    accept: ".json",
  });

  function importAgent() {
    open();
  }

  watchEffect(() => {
    const file = files?.value?.[0];

    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result as string;
      const jsonParser = createJsonParser(z.any());
      const data = jsonParser.safeParse(text);

      if (!data.success) {
        console.error(data.error);
        toaster.error($t("invalid_file"));
        return;
      }

      // Remove agent deploy fields if they exist (compatibility with old exports)
      const cleanedData = omit(data.data, [
        "agent_deploy_whatsapp",
        "agent_deploy_chat_widget",
        "agent_deploy_sip",
        "deployments",
      ]);

      // Ensure post_analysis is properly handled
      const agentData = {
        ...cleanedData,
        post_analysis: Array.isArray(cleanedData.post_analysis) ? cleanedData.post_analysis : [],
      } as AgentImport;

      // AI-1179: a connection_id is only valid inside the source project. Relink
      // each MCP server against this project's connections (id, then url),
      // otherwise mark it as needing a connection. A failed list never blocks
      // the import.
      if (agentData.mcp_servers?.length) {
        const { servers, unresolvedCount } = await resolveImportedMcpServersFromApi(
          agentData.mcp_servers,
          { headers: projectStore.getProjectHeader() },
        );
        agentData.mcp_servers = servers;
        if (unresolvedCount > 0)
          toaster.warning($t("agents:mcp_import_unresolved", { count: unresolvedCount }));
      }

      options.setAgent(agentData);

      reset();
    };

    reader.readAsText(file);
  });

  return {
    importAgent,
  };
};
